import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import ExcelJS from 'exceljs';
import { EmployeeHelpShell } from '../common/EmployeeCodeLegendPanel';
import { ListenButton } from '../common/AudioGuide';
import EmployeeImportConfirmModal from '../Accounting/EmployeeImportConfirmModal';
import { parseEmployeeImportRows, buildEmployeeImportPreview, incomingToBulkChanges } from '../../utils/employeeImportPreview';

// Row renderer for virtualized list - defined outside component to prevent re-creation
// Uses react-window v2 API: props are passed directly via rowProps
const EMPLOYEE_STATUSES = [
  { value: 'active', label: 'Active', color: '#047857', bg: '#d1fae5' },
  { value: 'terminated', label: 'Terminated', color: '#b91c1c', bg: '#fee2e2' },
  { value: 'on_leave', label: 'On Leave', color: '#b45309', bg: '#fef3c7' },
];

const VirtualRow = ({ index, style, rows, onCellEdit, onDeleteRow }) => {
  const row = rows?.[index];
  if (!row) return null;
  
  const rowId = row.isNew ? row.tempId : row.emp_code;
  const statusValue = row.is_active === false ? (row.status || 'terminated') : (row.status || 'active');
  const statusConfig = EMPLOYEE_STATUSES.find(s => s.value === statusValue) || EMPLOYEE_STATUSES[0];
  
  return (
    <div style={{
      ...style,
      display: 'flex',
      alignItems: 'center',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: row.isNew ? '#f0fdf4' : (index % 2 === 0 ? 'white' : '#f9fafb')
    }}>
      {/* Emp Code - editable only for new rows */}
      <div style={{ width: 100, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.isNew ? (row.emp_code === row.tempId ? '' : row.emp_code) : row.emp_code}
          onChange={(e) => onCellEdit(rowId, 'emp_code', e.target.value)}
          readOnly={!row.isNew}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem',
            backgroundColor: row.isNew ? 'white' : '#f3f4f6',
            color: row.isNew ? 'inherit' : '#6b7280'
          }}
          placeholder={row.isNew ? 'Code' : ''}
        />
      </div>
      {/* First Name */}
      <div style={{ width: 150, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.first_name || ''}
          onChange={(e) => onCellEdit(rowId, 'first_name', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}
          placeholder="First Name"
        />
      </div>
      {/* Last Name */}
      <div style={{ width: 150, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.last_name || ''}
          onChange={(e) => onCellEdit(rowId, 'last_name', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}
          placeholder="Last Name"
        />
      </div>
      {/* Email */}
      <div style={{ width: 200, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="email"
          value={row.empl_email || ''}
          onChange={(e) => onCellEdit(rowId, 'empl_email', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.8rem',
            boxSizing: 'border-box'
          }}
          placeholder="email@company.com"
        />
      </div>
      {/* Trade */}
      <div style={{ width: 120, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.trade || ''}
          onChange={(e) => onCellEdit(rowId, 'trade', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem',
            boxSizing: 'border-box'
          }}
          placeholder="Trade"
        />
      </div>
      {/* Payroll Company */}
      <div style={{ flex: 1, minWidth: 0, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={row.payroll_company || ''}
          onChange={(e) => onCellEdit(rowId, 'payroll_company', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}
          placeholder="Payroll Co."
        />
      </div>
      {/* Status */}
      <div style={{ width: 120, flexShrink: 0, padding: '8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>
        <select
          value={statusValue}
          onChange={(e) => onCellEdit(rowId, 'status', e.target.value)}
          style={{
            width: '100%',
            padding: '4px 6px',
            border: `1px solid ${statusConfig.color}40`,
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '500',
            backgroundColor: statusConfig.bg,
            color: statusConfig.color,
            cursor: 'pointer',
            appearance: 'auto'
          }}
        >
          {EMPLOYEE_STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
      {/* Actions */}
      <div style={{ width: 60, flexShrink: 0, padding: '8px', boxSizing: 'border-box', textAlign: 'center' }}>
        <button
          onClick={() => onDeleteRow(rowId)}
          style={{
            background: 'none',
            border: 'none',
            color: '#dc2626',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
          title="Delete"
        >
          🗑️
        </button>
      </div>
    </div>
  );
};

/**
 * EmployeesAdmin Component
 * Manages employees within the Time Tracker Admin modal.
 * Features: Virtualized editable table, import/export, manual entry with name formatting.
 * Names are stored as "Lastname, Firstname" but displayed as separate fields.
 */
const EmployeesAdmin = ({ onBack, isEmbedded = false }) => {
  const { instance, accounts } = useMsal();
  const { toast, showToast } = useToast();
  
  // State
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedRows, setEditedRows] = useState({}); // { emp_code: { first_name, last_name, trade, payroll_company } }
  const [newRows, setNewRows] = useState([]); // Array of new rows not yet saved
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const [listWidth, setListWidth] = useState(800);

  // Entra sync state
  const [entraModalOpen, setEntraModalOpen] = useState(false);
  const [entraUsers, setEntraUsers] = useState([]);
  const [entraLoading, setEntraLoading] = useState(false);
  const [entraSelected, setEntraSelected] = useState(new Set());
  const [entraSyncing, setEntraSyncing] = useState(false);
  const [entraSearch, setEntraSearch] = useState('');
  const [entraFilter, setEntraFilter] = useState('all'); // 'all', 'new', 'existing'

  // Stale employee review state
  const [syncStatus, setSyncStatus] = useState(null);
  const [staleEmployees, setStaleEmployees] = useState([]);
  const [staleCount, setStaleCount] = useState(0);
  const [showStalePanel, setShowStalePanel] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importIncoming, setImportIncoming] = useState([]);
  const [importingFile, setImportingFile] = useState(false);

  // Update list width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (listContainerRef.current) {
        setListWidth(listContainerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Fetch employees on mount
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/employees'
      );
      if (response.data.success) {
        setEmployees(response.data.data || []);
        setEditedRows({});
        setNewRows([]);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      showToast('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Fetch sync status and stale employees on mount
  const fetchSyncStatus = useCallback(async () => {
    try {
      const [statusRes, reviewRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/entra-sync/status'),
        authenticatedApiCall(instance, accounts, 'GET', '/entra-sync/review'),
      ]);
      if (statusRes.data.success) setSyncStatus(statusRes.data.data);
      if (reviewRes.data.success) {
        setStaleEmployees(reviewRes.data.data || []);
        setStaleCount(reviewRes.data.count || (reviewRes.data.data || []).length);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const handleDeactivateStale = async (empCodes) => {
    if (!window.confirm(`Deactivate ${empCodes.length} employee(s) not found in Entra ID?`)) return;
    setDeactivating(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/entra-sync/deactivate', { empCodes });
      if (response.data.success) {
        showToast(`Deactivated ${response.data.data.deactivated} employee(s)`, 'success');
        fetchEmployees();
        fetchSyncStatus();
      }
    } catch (error) {
      console.error('Error deactivating employees:', error);
      showToast('Failed to deactivate employees', 'error');
    } finally {
      setDeactivating(false);
    }
  };

  const handleManualSync = async () => {
    setEntraSyncing(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/entra-sync/run-now');
      if (response.data.success) {
        const d = response.data.data;
        showToast(`Sync complete: ${d.inserted} new, ${d.updated} updated, ${d.staleCount} stale`, 'success');
        fetchEmployees();
        fetchSyncStatus();
      }
    } catch (error) {
      console.error('Error running manual sync:', error);
      showToast('Failed to run Entra sync', 'error');
    } finally {
      setEntraSyncing(false);
    }
  };

  // Handle cell edit
  const handleCellEdit = useCallback((empCode, field, value) => {
    if (empCode.toString().startsWith('new_')) {
      // Edit new row
      setNewRows(prev => prev.map(row => 
        row.tempId === empCode ? { ...row, [field]: value } : row
      ));
    } else {
      // Edit existing row
      setEditedRows(prev => ({
        ...prev,
        [empCode]: {
          ...prev[empCode],
          [field]: value,
          emp_code: empCode
        }
      }));
    }
  }, []);

  // Add new row (inserted at top)
  const handleAddRow = () => {
    const tempId = `new_${Date.now()}`;
    setNewRows(prev => [{
      tempId,
      emp_code: '',
      first_name: '',
      last_name: '',
      empl_email: '',
      trade: '',
      division: '',
      payroll_company: '',
      status: 'active',
      is_active: true,
      isNew: true
    }, ...prev]);
  };

  // Delete row
  const handleDeleteRow = useCallback(async (empCode) => {
    if (empCode.toString().startsWith('new_')) {
      // Remove unsaved new row
      setNewRows(prev => prev.filter(row => row.tempId !== empCode));
      return;
    }
    
    if (!window.confirm('Deactivate this employee? The master row stays. Accounting can turn them active again.')) return;
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/employees/${encodeURIComponent(empCode)}`
      );
      if (response.data.success) {
        const updated = response.data.data;
        setEmployees(prev => prev.map(row => (
          row.emp_code === empCode
            ? { ...row, ...(updated || {}), is_active: false, status: 'terminated' }
            : row
        )));
        showToast('Employee deactivated. The master row stays.', 'success');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      showToast('Failed to deactivate employee', 'error');
    }
  }, [instance, accounts, showToast]);

  // Save all changes
  const handleSaveChanges = async () => {
    const changes = [];
    
    // Collect edited existing rows
    Object.entries(editedRows).forEach(([empCode, edits]) => {
      const original = employees.find(e => e.emp_code === empCode);
      if (original) {
        const status = edits.status !== undefined ? edits.status : (original.status || (original.is_active === false ? 'terminated' : 'active'));
        changes.push({
          emp_code: empCode,
          first_name: edits.first_name !== undefined ? edits.first_name : original.first_name,
          last_name: edits.last_name !== undefined ? edits.last_name : original.last_name,
          empl_email: edits.empl_email !== undefined ? edits.empl_email : original.empl_email,
          trade: edits.trade !== undefined ? edits.trade : original.trade,
          division: edits.division !== undefined ? edits.division : original.division,
          payroll_company: edits.payroll_company !== undefined ? edits.payroll_company : original.payroll_company,
          status,
          is_active: status === 'active',
          isNew: false
        });
      }
    });
    
    // Collect new rows
    newRows.forEach(row => {
      if (row.emp_code?.trim() && row.first_name?.trim() && row.last_name?.trim()) {
        const status = row.status || 'active';
        changes.push({
          emp_code: row.emp_code,
          first_name: row.first_name,
          last_name: row.last_name,
          empl_email: row.empl_email,
          trade: row.trade,
          division: row.division,
          payroll_company: row.payroll_company,
          status,
          is_active: status === 'active',
          isNew: true
        });
      }
    });
    
    if (changes.length === 0) {
      showToast('No changes to save', 'info');
      return;
    }
    
    // Validate new rows have required fields
    for (const change of changes) {
      if (!change.first_name?.trim() || !change.last_name?.trim()) {
        showToast('First name and last name are required for all employees', 'error');
        return;
      }
      if (change.isNew && !change.emp_code?.trim()) {
        showToast('Employee code is required for new employees', 'error');
        return;
      }
    }
    
    setSaving(true);
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/employees/bulk-save',
        { changes }
      );
      
      if (response.data.success) {
        showToast(`Saving ${changes.length} changes...`, 'info');
        // Refresh data after a short delay to allow queue to process
        setTimeout(() => {
          fetchEmployees();
          showToast('Changes saved successfully', 'success');
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Import Excel file — preview first, then write after the user agrees
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadProgress('Parsing file...');
    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        showToast('No worksheet found in file', 'error');
        setUploadProgress(null);
        return;
      }
      const headers = [];
      worksheet.getRow(1).eachCell((cell, col) => {
        headers[col - 1] = cell.value != null ? String(cell.value) : '';
      });
      const dataRows = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const vals = [];
        row.eachCell({ includeEmpty: true }, (cell, col) => {
          const v = cell.value;
          vals[col - 1] = v && typeof v === 'object' && v.text ? v.text : v;
        });
        dataRows.push(vals);
      });
      const parsed = parseEmployeeImportRows(headers, dataRows);
      if (parsed.error) {
        showToast(parsed.error, 'error');
        setUploadProgress(null);
        return;
      }
      setImportIncoming(parsed.rows);
      setImportFileName(file.name);
      setImportPreview(buildEmployeeImportPreview(parsed.rows, employees));
      setUploadProgress(null);
    } catch (error) {
      console.error('Error importing file:', error);
      showToast('Failed to import file', 'error');
      setUploadProgress(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmEmployeeImport = async () => {
    if (!importIncoming.length) return;
    setImportingFile(true);
    try {
      const existingCodes = new Set(employees.map((r) => r.emp_code));
      const changes = incomingToBulkChanges(importIncoming, { existingCodes });
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/employees/bulk-save', { changes });
      if (response.data.success) {
        showToast(response.data.message || `Imported ${changes.length} row(s)`, 'success');
        setImportPreview(null);
        setImportIncoming([]);
        await fetchEmployees();
      } else {
        showToast(response.data.message || 'Import failed', 'error');
      }
    } catch (error) {
      console.error('Error importing file:', error);
      showToast(error.response?.data?.message || 'Failed to import file', 'error');
    } finally {
      setImportingFile(false);
    }
  };

  // Export to Excel (direct download of the full master list)
  const handleExport = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/employees/export-direct',
        null,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employee_master_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting employees:', error);
      showToast('Failed to export employees', 'error');
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/employees/template',
        null,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      showToast('Failed to download template', 'error');
    }
  };

  // Fetch Entra users for preview
  const handleOpenEntraSync = async () => {
    setEntraModalOpen(true);
    setEntraLoading(true);
    setEntraUsers([]);
    setEntraSelected(new Set());
    setEntraSearch('');
    setEntraFilter('all');
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/employees/entra-users');
      if (response.data.success) {
        setEntraUsers(response.data.data || []);
        // Pre-select users that are new (not already in the system)
        const newIds = (response.data.data || [])
          .filter(u => !u.already_exists && u.employee_id)
          .map(u => u.azure_object_id);
        setEntraSelected(new Set(newIds));
      } else {
        showToast(response.data.message || 'Failed to load Entra users', 'error');
      }
    } catch (error) {
      console.error('Error fetching Entra users:', error);
      const msg = error.response?.data?.message || 'Failed to fetch users from Entra ID';
      showToast(msg, 'error');
    } finally {
      setEntraLoading(false);
    }
  };

  const handleEntraSelectAll = (checked) => {
    if (checked) {
      setEntraSelected(new Set(filteredEntraUsers.map(u => u.azure_object_id)));
    } else {
      setEntraSelected(new Set());
    }
  };

  const handleEntraToggle = (azureId) => {
    setEntraSelected(prev => {
      const next = new Set(prev);
      if (next.has(azureId)) next.delete(azureId);
      else next.add(azureId);
      return next;
    });
  };

  const handleEntraSync = async () => {
    const toSync = entraUsers.filter(u => entraSelected.has(u.azure_object_id));
    if (toSync.length === 0) {
      showToast('Select at least one user to sync', 'info');
      return;
    }
    setEntraSyncing(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/employees/sync-from-entra', { users: toSync });
      if (response.data.success) {
        const d = response.data.data;
        showToast(`Synced: ${d.inserted} new, ${d.updated} updated${d.errors > 0 ? `, ${d.errors} errors` : ''}`, 'success');
        setEntraModalOpen(false);
        fetchEmployees();
      }
    } catch (error) {
      console.error('Error syncing Entra users:', error);
      showToast('Failed to sync employees from Entra', 'error');
    } finally {
      setEntraSyncing(false);
    }
  };

  const filteredEntraUsers = useMemo(() => {
    let list = entraUsers;
    if (entraFilter === 'new') list = list.filter(u => !u.already_exists);
    else if (entraFilter === 'existing') list = list.filter(u => u.already_exists);
    if (entraSearch) {
      const term = entraSearch.toLowerCase();
      list = list.filter(u =>
        u.display_name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.employee_id?.toLowerCase().includes(term) ||
        u.department?.toLowerCase().includes(term) ||
        u.job_title?.toLowerCase().includes(term)
      );
    }
    return list;
  }, [entraUsers, entraFilter, entraSearch]);

  // Combine existing and new rows for display
  const allRows = useMemo(() => {
    const existingWithEdits = employees.map(row => ({
      ...row,
      ...(editedRows[row.emp_code] || {}),
      isNew: false
    }));
    
    const newWithIds = newRows.map(row => ({
      ...row,
      emp_code: row.emp_code || row.tempId,
      displayId: row.tempId,
      isNew: true
    }));
    
    let combined = [...newWithIds, ...existingWithEdits];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      combined = combined.filter(row => {
        const statusLabel = (EMPLOYEE_STATUSES.find(s => s.value === row.status)?.label || 'Active').toLowerCase();
        return row.emp_code?.toLowerCase().includes(term) ||
          row.first_name?.toLowerCase().includes(term) ||
          row.last_name?.toLowerCase().includes(term) ||
          row.empl_email?.toLowerCase().includes(term) ||
          row.trade?.toLowerCase().includes(term) ||
          row.payroll_company?.toLowerCase().includes(term) ||
          statusLabel.includes(term);
      });
    }
    
    return combined;
  }, [employees, editedRows, newRows, searchTerm]);

  // Check if there are unsaved changes
  const hasChanges = Object.keys(editedRows).length > 0 || newRows.some(r => r.emp_code?.trim());

  // Item data to pass to each row via react-window
  const itemData = useMemo(() => ({
    rows: allRows,
    onCellEdit: handleCellEdit,
    onDeleteRow: handleDeleteRow
  }), [allRows, handleCellEdit, handleDeleteRow]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {!isEmbedded && (
            <button
              onClick={onBack}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.25rem',
                cursor: 'pointer',
                color: '#6b7280'
              }}
            >
              ←
            </button>
          )}
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Employees Management
          </h2>
        </div>
      </div>
      <div style={{
        padding: '8px 24px', fontSize: 12, color: '#92400e',
        background: '#fffbeb', borderBottom: '1px solid #fde68a',
      }}>
        Same Hiring / Layoff master as Accounting (footer ~1383). Entra (~227 logins) is a different circle — not “227 of 1383.” The yellow banner is active people with no Entra match (real count, not a 200-row cap). Full H R help is on this ❓ Help.
      </div>
      
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: 'white'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem',
              width: '200px'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setHelpOpen(true)}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            ❓ Help
          </button>
          <button
            onClick={handleDownloadTemplate}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            📄 Template
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            📥 Import
          </button>
          <button
            onClick={handleExport}
            disabled={employees.length === 0}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: employees.length > 0 ? '#f3f4f6' : '#e5e7eb',
              color: employees.length > 0 ? '#374151' : '#9ca3af',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: employees.length > 0 ? 'pointer' : 'not-allowed',
              fontSize: '0.8rem'
            }}
          >
            📤 Export
          </button>
          <button
            onClick={handleAddRow}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            ➕ Add New
          </button>
          <button
            onClick={handleOpenEntraSync}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '500'
            }}
            title="Import employees from Microsoft Entra ID"
          >
            ☁ Sync from Entra
          </button>
        </div>
      </div>
      
      {/* Upload Progress */}
      {uploadProgress && (
        <div style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#eff6ff',
          borderBottom: '1px solid #bfdbfe',
          color: '#1e40af',
          fontSize: '0.875rem'
        }}>
          {uploadProgress}
        </div>
      )}

      {/* Sync Status Bar */}
      <div style={{
        padding: '0.5rem 1.5rem',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.75rem',
        color: '#6b7280'
      }}>
        <span>
          {syncStatus ? (
            <>
              Last Entra sync: {new Date(syncStatus.finished_at).toLocaleString()}
              {' '}&middot;{' '}{syncStatus.total_entra} Entra users
              {' '}&middot;{' '}{syncStatus.inserted} new, {syncStatus.updated} updated
            </>
          ) : 'No Entra sync has run yet'}
        </span>
        <button
          onClick={handleManualSync}
          disabled={entraSyncing}
          style={{
            padding: '0.25rem 0.75rem',
            backgroundColor: entraSyncing ? '#9ca3af' : '#0078d4',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: entraSyncing ? 'not-allowed' : 'pointer',
            fontSize: '0.75rem'
          }}
        >
          {entraSyncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {/* Stale Employee Warning */}
      {staleEmployees.length > 0 && (
        <div style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#fef3c7',
          borderBottom: '1px solid #fbbf24',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '0.8rem', color: '#92400e' }}>
            {staleCount} active employee(s) not found in Entra ID — field craft, contractors, or people with no login email. Entra (~227) is the office login list, not a subset of this master. Review shows the first {staleEmployees.length}.
          </span>
          <button
            onClick={() => setShowStalePanel(!showStalePanel)}
            style={{
              padding: '0.25rem 0.75rem',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '500'
            }}
          >
            {showStalePanel ? 'Hide' : 'Review'}
          </button>
        </div>
      )}

      {/* Stale Employees Review Panel */}
      {showStalePanel && staleEmployees.length > 0 && (
        <div style={{
          padding: '1rem 1.5rem',
          backgroundColor: '#fffbeb',
          borderBottom: '1px solid #fbbf24',
          maxHeight: '250px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#92400e' }}>
              Employees not found in Entra ID
            </span>
            <button
              onClick={() => handleDeactivateStale(staleEmployees.map(e => e.emp_code))}
              disabled={deactivating}
              style={{
                padding: '0.3rem 0.75rem',
                backgroundColor: deactivating ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: deactivating ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem'
              }}
            >
              {deactivating ? 'Deactivating...' : `Deactivate All (${staleEmployees.length})`}
            </button>
          </div>
          <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #fbbf24' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#92400e' }}>Emp Code</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#92400e' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#92400e' }}>Email</th>
                <th style={{ textAlign: 'center', padding: '4px 8px', color: '#92400e' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {staleEmployees.map(emp => (
                <tr key={emp.emp_code} style={{ borderBottom: '1px solid #fde68a' }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{emp.emp_code}</td>
                  <td style={{ padding: '4px 8px' }}>{emp.emp_name}</td>
                  <td style={{ padding: '4px 8px', color: '#6b7280' }}>{emp.empl_email || '—'}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleDeactivateStale([emp.emp_code])}
                      disabled={deactivating}
                      style={{
                        padding: '2px 8px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '0.7rem'
                      }}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', padding: '0' }}>
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280'
          }}>
            Loading...
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Table Header */}
            <div style={{
              display: 'flex',
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f3f4f6',
              fontWeight: '600',
              fontSize: '0.875rem',
              paddingRight: '17px'
            }}>
              <div style={{ width: 100, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>Emp Code</div>
              <div style={{ width: 150, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>First Name</div>
              <div style={{ width: 150, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>Last Name</div>
              <div style={{ width: 200, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>Email</div>
              <div style={{ width: 120, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>Trade</div>
              <div style={{ flex: 1, minWidth: 0, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>Payroll Co.</div>
              <div style={{ width: 120, flexShrink: 0, padding: '10px 8px', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' }}>Status</div>
              <div style={{ width: 60, flexShrink: 0, padding: '10px 8px', boxSizing: 'border-box', textAlign: 'center' }}>Actions</div>
            </div>
            
            {/* Virtualized Table Body */}
            <div ref={listContainerRef} style={{ flex: 1 }}>
              {allRows.length === 0 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#6b7280'
                }}>
                  No employees found. Click "Add New" or "Import" to add employees.
                </div>
              ) : (
                <List
                  rowComponent={VirtualRow}
                  rowCount={allRows.length}
                  rowHeight={45}
                  rowProps={itemData}
                  overscanCount={5}
                  style={{ height: 400, width: '100%' }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {`${allRows.length} employees`}
          {hasChanges && <span style={{ color: '#f59e0b', marginLeft: '1rem' }}>• Unsaved changes</span>}
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {!isEmbedded && (
            <button
              onClick={onBack}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Back
            </button>
          )}
          <button
            onClick={handleSaveChanges}
            disabled={!hasChanges || saving}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: hasChanges ? '#2563eb' : '#9ca3af',
              color: 'white',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#fee2e2' : toast.type === 'success' ? '#d1fae5' : '#dbeafe',
          color: toast.type === 'error' ? '#b91c1c' : toast.type === 'success' ? '#047857' : '#1e40af',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1100
        }}>
          {toast.message}
        </div>
      )}

      {/* Help with Legend tab */}
      <EmployeeHelpShell
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Employees Management — Help"
        accent="#1e3a5f"
        guideId="hiring-layoff-help"
        ListenButton={ListenButton}
        processContent={(
          <div style={{ fontSize: 13, lineHeight: 1.6, color: '#374151' }}>
            <p style={{ marginTop: 0 }}>
              This screen edits <strong>the single master employee list</strong> (<code>timetracker.lt_employee</code>) —
              the same list shown in Accounting → Hiring / Layoff. There is no second roster.
            </p>
            <ul>
              <li><strong>Hiring:</strong> Tanya adds the person (4-digit code + name) → Jordan creates the Entra email →
                the daily Entra sync fills the email and creates the portal user → Time Tracker access is automatic.</li>
              <li><strong>Layoff / leave:</strong> change Status — portal access is revoked and the person leaves all
                active-only dropdowns. History is preserved.</li>
              <li><strong>Sync from Entra</strong> imports users by Employee ID or email — it never invents fake emails.</li>
              <li>Employee codes are always 4 digits. Emails must be unique among active / on-leave people; a terminated row can keep the old mailbox.</li>
              <li>Department / discipline / role codes are on the Legend tab.</li>
            </ul>
          </div>
        )}
      />

      {/* Entra Sync Modal */}
      {entraModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '900px',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                  Sync Employees from Entra ID
                </h3>
                {!entraLoading && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                    {entraUsers.length} users found &middot;{' '}
                    {entraUsers.filter(u => !u.already_exists).length} new &middot;{' '}
                    {entraUsers.filter(u => u.already_exists).length} already in system
                  </p>
                )}
              </div>
              <button onClick={() => setEntraModalOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>
                &times;
              </button>
            </div>

            {/* Modal Toolbar */}
            <div style={{
              padding: '0.75rem 1.5rem', borderBottom: '1px solid #e5e7eb',
              display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', backgroundColor: '#f9fafb'
            }}>
              <input
                type="text"
                placeholder="Search by name, email, ID, department..."
                value={entraSearch}
                onChange={e => setEntraSearch(e.target.value)}
                style={{
                  padding: '0.4rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px',
                  fontSize: '0.8rem', width: '260px'
                }}
              />
              <select value={entraFilter} onChange={e => setEntraFilter(e.target.value)}
                style={{ padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.8rem' }}>
                <option value="all">All Users</option>
                <option value="new">New Only</option>
                <option value="existing">Already Exists</option>
              </select>
              <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filteredEntraUsers.length > 0 && filteredEntraUsers.every(u => entraSelected.has(u.azure_object_id))}
                  onChange={e => handleEntraSelectAll(e.target.checked)}
                />
                Select all ({filteredEntraUsers.length})
              </label>
              <span style={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: '500', marginLeft: 'auto' }}>
                {entraSelected.size} selected
              </span>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
              {entraLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#6b7280' }}>
                  Loading users from Entra ID...
                </div>
              ) : filteredEntraUsers.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#6b7280' }}>
                  No users match the current filter.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', textAlign: 'center', width: '40px', borderBottom: '2px solid #e5e7eb' }}></th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Name</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Employee ID</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Job Title</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Department</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntraUsers.map((user, idx) => (
                      <tr key={user.azure_object_id}
                        style={{
                          backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleEntraToggle(user.azure_object_id)}>
                        <td style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                          <input type="checkbox" checked={entraSelected.has(user.azure_object_id)}
                            onChange={() => handleEntraToggle(user.azure_object_id)} onClick={e => e.stopPropagation()} />
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: '500' }}>
                          {user.display_name}
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', color: '#4b5563' }}>
                          {user.email}
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>
                          {user.employee_id || <span style={{ color: '#d1d5db' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', color: '#4b5563' }}>
                          {user.job_title || '—'}
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', color: '#4b5563' }}>
                          {user.department || '—'}
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                          {user.already_exists ? (
                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '500', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                              Exists
                            </span>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '500', backgroundColor: '#d1fae5', color: '#047857' }}>
                              New
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb'
            }}>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280', maxWidth: '55%' }}>
                Users with an Employee ID will be created/updated by that code.
                Users without an Employee ID will be matched by email only.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => setEntraModalOpen(false)}
                  style={{
                    padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '6px',
                    backgroundColor: 'white', color: '#374151', cursor: 'pointer', fontSize: '0.875rem'
                  }}>
                  Cancel
                </button>
                <button onClick={handleEntraSync} disabled={entraSelected.size === 0 || entraSyncing}
                  style={{
                    padding: '0.5rem 1rem', border: 'none', borderRadius: '6px',
                    backgroundColor: entraSelected.size > 0 ? '#0078d4' : '#9ca3af',
                    color: 'white', cursor: entraSelected.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.875rem', fontWeight: '500'
                  }}>
                  {entraSyncing ? 'Syncing...' : `Sync ${entraSelected.size} User${entraSelected.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <EmployeeImportConfirmModal
        open={!!importPreview}
        preview={importPreview}
        fileName={importFileName}
        applying={importingFile}
        onCancel={() => { setImportPreview(null); setImportIncoming([]); }}
        onConfirm={confirmEmployeeImport}
      />
    </div>
  );
};

export default EmployeesAdmin;
